import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import useAuth from "../../../hooks/useAuth";
import { useSnack } from "../../../hooks/useSnack";
import lessonServices from "../../../services/lessonServices";
import studentServices from "../../../services/studentServices";
import TeacherPythonIDEWrapper from "../../IDEs/wrappers/teacher/TeacherPythonIDEWrapper";
import TeacherSmoothyIDEWrapper from "../../IDEs/wrappers/teacher/TeacherSmoothyIDEWrapper";
import TeacherWebIDEWrapper from "../../IDEs/wrappers/teacher/TeacherWebIDEWrapper";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";

const StudentChallengeSelectLevelModal = forwardRef((_, ref) => {
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const user = useAuth();

  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [lessonID, setLessonID] = useState("");
  const [lessonTitle, setLessonTitle] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal(lessonID, lessonTitle) {
      setLessonID(lessonID);
      setLessonTitle(lessonTitle);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
      setSelectedChallenge(null);
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
    setSelectedChallenge(null);
  };

  const handleChallengeSelect = (challenge) => {
    setSelectedChallenge(challenge);
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await studentServices.getStudentProjects(
        3,
        user?.IDEType.toLowerCase(),
        user?.id,
        lessonID
      );
      setChallenges(response);
      if (response.length > 0) {
        setSelectedChallenge(response[0]);
      }
    } catch (error) {
      showSnack(error, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  return (
    <Modal isFullScreen isOpen={isOpen} onClose={handleCloseModal}>
      {loading ? (
        <LoadingSpinner />
      ) : challenges.length == 0 ? (
        <div className="mt-52 flex items-center justify-center font-semibold text-blue-500">
          {t("you_did_not_take_any_challenge_yet")}
        </div>
      ) : (
        <div className="flex h-full">
          {/* Sidebar with challenge list */}
          <div className="w-1/4 rounded-md border bg-background p-4 dark:border-dark-border dark:bg-dark-background">
            <h2 className="mb-4 text-lg font-bold">{lessonTitle}</h2>

            {/* Group challenges by difficulty */}
            {["easy", "hard"].map((difficulty) => {
              const filteredChallenges = challenges.filter(
                (challenge) => challenge.difficulty === difficulty
              );

              if (filteredChallenges.length === 0) return null;

              return (
                <div key={difficulty} className="mb-4">
                  <h3 className="text-md mb-2 font-semibold capitalize">{t(difficulty)}</h3>
                  <ul>
                    {filteredChallenges.map((challenge) => (
                      <li
                        key={challenge.id}
                        className={`mb-2 cursor-pointer rounded-md p-2 shadow-md transition-all ${
                          selectedChallenge?.id === challenge.id
                            ? "bg-blue-500 text-white dark:bg-blue-500"
                            : "bg-white hover:bg-gray-50 dark:bg-dark-surface"
                        }`}
                        onClick={() => handleChallengeSelect(challenge)}
                      >
                        <div className="flex items-center gap-2">
                          <span>{challenge.name}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* IDE Container */}
          <div className="w-3/4 p-4">
            {selectedChallenge?.code_type === "smoothy" && (
              <TeacherSmoothyIDEWrapper
                type="solutions"
                projectGoal={{
                  projectGoal: selectedChallenge?.challenge_statement,
                  name: selectedChallenge?.name,
                  image: selectedChallenge?.image,
                }}
                codeData={selectedChallenge}
              />
            )}

            {selectedChallenge?.code_type === "web" && (
              <TeacherWebIDEWrapper
                type="solutions"
                projectGoal={{
                  projectGoal: selectedChallenge?.challenge_statement,
                  name: selectedChallenge?.name,
                  image: selectedChallenge?.image,
                }}
                codeData={selectedChallenge}
              />
            )}

            {selectedChallenge?.code_type === "python" && (
              <TeacherPythonIDEWrapper
                type="solutions"
                projectGoal={{
                  projectGoal: selectedChallenge?.challenge_statement,
                  name: selectedChallenge?.name,
                  image: selectedChallenge?.image,
                }}
                codeData={selectedChallenge}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
});

export default StudentChallengeSelectLevelModal;
